import React, { useEffect, useState } from 'react'
import { useGenerateAdvertiserReportMutation, useGetAdvertiserReportQuery } from '../../../../../generated/graphql'
import { useTranslation } from 'react-i18next'
import * as stringKey from './manage_advertisers_i18n_mappings'
import { InlineMessage, Text, Link } from '@amzn/storm-ui'
import { PARTNER_ID_HEADER_KEY } from '../../../../../util/app_initialization/apollo_client/stagedive_apollo_client_provider'
import { toNullable } from 'fp-ts/es6/Option'
import { skeletonWhileLoading } from '../../../../../util/skeleton_loading'
import { LoadingButton } from '@amzn/aapn-react-components'
import { MetricEvent } from '@amzn/aapn-front-end-metrics'
import { useLoginContext } from 'stagedive/LoginContext'
import { useLanguageContext } from 'stagedive/LanguageContext'
import { DownloadIcon, ExportReportContainer, ReportStatusContainer } from './manage_advertisers_v2.styles'

const POLL_INTERVAL = 5000

export const AdvertiserReportExport: React.FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { loginContext } = useLoginContext()
  const partnerAccountId = toNullable(loginContext.partnerProfileId) ?? ''
  const [isPolling, setPolling] = useState(false)

  const {
    loading: adReportLoading,
    error: adReportError,
    data: adReportData,
    startPolling,
    stopPolling,
  } = useGetAdvertiserReportQuery({
    context: {
      headers: {
        [PARTNER_ID_HEADER_KEY]: partnerAccountId,
      },
    },
    fetchPolicy: 'network-only',
    pollInterval: POLL_INTERVAL,
  })

  const [generateReport, { loading: generateReportLoading, error: generateReportError }] = useGenerateAdvertiserReportMutation()

  const skeletonize = skeletonWhileLoading(adReportLoading)

  useEffect(() => {
    if (adReportData?.advertiserReport?.__typename === 'CompletedReport' || adReportData?.advertiserReport?.__typename === 'NoReport' || adReportError) {
      stopPolling()
      setPolling(false)
    }
  }, [adReportData, adReportError, stopPolling, setPolling])

  const handleGenerateReport = (): void => {
    generateReport({
      context: {
        headers: {
          [PARTNER_ID_HEADER_KEY]: partnerAccountId,
        },
      },
    })
      .then((r) => {
        if (r?.data?.generateAdvertiserReport?.__typename === 'PendingReport') {
          startPolling(POLL_INTERVAL)
          setPolling(true)
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {})
  }

  const reportStatusMessage = ((): JSX.Element | null => {
    if (adReportData?.advertiserReport?.__typename === 'PendingReport' || isPolling || generateReportLoading) {
      return <InlineMessage messageType={'info'} message={t(stringKey.ADVERTISERS_RUNNING_EXPORT)} />
    } else if (adReportData?.advertiserReport?.__typename === 'CompletedReport') {
      const reportCreationDate = new Date(adReportData?.advertiserReport?.creationDate)
      const formattedReportCreationDate = reportCreationDate.toLocaleString(language, { year: 'numeric', month: 'long', day: 'numeric' })
      const formattedReportCreationTime = reportCreationDate.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })

      return (
        <Text>
          <DownloadIcon type="download" />
          <Link href={adReportData?.advertiserReport?.location} data-id="download-advertiser-report-button" data-publish-metrics={MetricEvent.Click}>
            {t(stringKey.ADVERTISERS_DOWNLOAD_EXPORT)}
          </Link>
          <Text type="span">&nbsp;|&nbsp;{formattedReportCreationDate}</Text>
          <Text type="span">&nbsp;-&nbsp;{formattedReportCreationTime}</Text>
        </Text>
      )
    } else if (adReportLoading) {
      return skeletonize(<></>, { width: 270, height: 15 })
    } else if (adReportData?.advertiserReport?.__typename === 'InvalidState' || adReportError || generateReportError) {
      return <InlineMessage messageType={'error'} message={t(stringKey.ADVERTISERS_ERROR_TRY_AGAIN)} />
    }
    return null
  })()

  return (
    <ExportReportContainer>
      <LoadingButton
        loadingLabel={t(stringKey.LOADING)}
        sendLabel={t(stringKey.ADVERTISERS_BTN_RUN_EXPORT)}
        isLoading={generateReportLoading}
        isDisabled={isPolling}
        secondary={true}
        onClickSend={handleGenerateReport}
        data-id="run-advertiser-report-button"
        data-publish-metrics={MetricEvent.Click}
      >
        {t(stringKey.ADVERTISERS_BTN_RUN_EXPORT)}
      </LoadingButton>
      <ReportStatusContainer>{reportStatusMessage}</ReportStatusContainer>
    </ExportReportContainer>
  )
}
